import type { AbsenceType } from '@lmc/ts-common'
import {
  Button,
  DownloadFile,
  FilterContainer,
  GroupedUsersMultiSelect,
  MultiSelect,
  RangePicker
} from '@lmc/ui-kit'
import type { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import useUsersList from 'screens/Rostering/hooks/useUsersList'
import styled from 'styled-components'

export type AbsencesFiltersValue = {
  rangePicker?: [Dayjs | null, Dayjs | null] | null
  absenceType?: string
  users?: string[]
}

type AbsencesFiltersProps = {
  all: boolean
  absenceTypes: AbsenceType[]
  value: Partial<AbsencesFiltersValue>
  onChange: (value: AbsencesFiltersValue) => void
  onDownloadCsv: () => void
}

const AbsencesFilters = ({
  absenceTypes,
  onChange,
  value,
  all,
  onDownloadCsv
}: AbsencesFiltersProps) => {
  const { t } = useTranslation()
  const absenceTypesOptions = absenceTypes?.map((absenceType) => ({
    value: absenceType.id,
    label: absenceType.name
  }))

  const { users } = useUsersList({})

  const handleInputChange = (newValue: Partial<AbsencesFiltersValue>) => {
    const updatedValue: AbsencesFiltersValue = {
      ...value,
      ...newValue
    }
    onChange(updatedValue)
  }

  return (
    <FilterContainer title={t('FILTERS')}>
      <Filters>
        <LeftContainer>
          <RangePicker
            value={value.rangePicker}
            onChange={(rangePicker) => handleInputChange({ rangePicker })}
          />
          {all && (
            <GroupedUsersMultiSelect
              users={users}
              title={t('TEAM_MEMBERS')}
              handleInputChange={(selectedOptions) =>
                handleInputChange({
                  users: selectedOptions as string[]
                })
              }
            />
          )}
          <MultiSelectWide
            options={absenceTypesOptions}
            hasClear
            hasSearch
            hasSelectAll
            title={t('ABSENCE_TYPE')}
            onChange={(absenceType: unknown) =>
              handleInputChange({
                absenceType: absenceType as string
              })
            }
          />
        </LeftContainer>
        <RightAlign>
          <Button onClick={onDownloadCsv} icon={<DownloadFile />}>
            {t('DOWNLOAD_CSV')}
          </Button>
        </RightAlign>
      </Filters>
    </FilterContainer>
  )
}

const Filters = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`

const LeftContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const RightAlign = styled.div`
  display: flex;
  align-items: center;
`

const MultiSelectWide = styled(MultiSelect)`
  min-width: 200px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: 100%;
  }
`

export default AbsencesFilters
